import { useTranslation } from '@mint-lib/i18n';
import { Upload } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import { Button, typographyVariants } from '@mint-lib/tw-components';

type Props = {
  isFileSystemEnabled: boolean;
  canUploadKBFile: boolean;
  onOpenUploadDialog: () => void;
};

const EmptyFolderState = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <div className="flex flex-col items-center gap-2">
      <Illustration
        className="size-40"
        data-testid="empty-folder-icon"
        component="IllustrationEmptyState"
      />
      <p className={typographyVariants({ variant: 'h4' })}>
        {t('This folder is empty')}
      </p>
    </div>
  );
};

const NoUploadsState = ({
  canUploadKBFile,
  onOpenUploadDialog,
}: Pick<Props, 'canUploadKBFile' | 'onOpenUploadDialog'>) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <div className="flex w-40 flex-col items-center gap-4">
      <Illustration
        className="size-40"
        data-testid="file-upload-icon"
        component="IllustrationFileUpload"
      />
      <p className={typographyVariants({ variant: 'h4' })}>
        {t('No uploaded file yet')}
      </p>
      <Button
        className="flex flex-row gap-2"
        onClick={onOpenUploadDialog}
        disabled={!canUploadKBFile}
      >
        <Upload />
        {t('Upload file')}
      </Button>
    </div>
  );
};

export const FolderErrorState = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4">
      <Illustration
        className="size-40"
        data-testid="folder-error-icon"
        component="IllustrationNoConnection"
      />
      <p className={typographyVariants({ variant: 'h4' })}>
        {t('This folder could not be loaded')}
      </p>
      <Button
        variant="outlined"
        data-dd-action-name="Knowledge base: retry loading folder"
        onClick={onRetry}
      >
        {t('Try again')}
      </Button>
    </div>
  );
};

/**
 * What fills the body when there is nothing to list. The file system speaks in
 * terms of the current folder; the legacy view only knows about uploads.
 */
const EmptyState = ({
  isFileSystemEnabled,
  canUploadKBFile,
  onOpenUploadDialog,
}: Props) => (
  <div className="flex h-full flex-col items-center justify-center">
    {isFileSystemEnabled ? (
      <EmptyFolderState />
    ) : (
      <NoUploadsState
        canUploadKBFile={canUploadKBFile}
        onOpenUploadDialog={onOpenUploadDialog}
      />
    )}
  </div>
);

export { EmptyState };
